import { useSubscription } from '@apollo/client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Loader } from 'components/Loader';

import { SupplierGlobalForm } from './SupplierGlobalForm';
import { SupplierOdooOnboarding } from './SupplierOdooOnboarding';

import { SUPPLIER_UPDATED_SUBSCRIPTION } from 'data/subscriptions/supplier';

import { SupplierFragmentFragment, SupplierUpdatedSubscription, useGetDetailedSupplierQuery } from 'data/__generated__';

type ParamTypes = {
    supplierId: string;
};

export const SupplierDetails = () => {
    const { supplierId = '' } = useParams<ParamTypes>();
    const [supplierUpdated, setSupplierUpdated] = useState<SupplierFragmentFragment | null>(null);
    const {
        loading: supplierLoading,
        data: supplierData,
        error: supplierError,
    } = useGetDetailedSupplierQuery({ variables: { supplierId } });
    useSubscription<SupplierUpdatedSubscription>(SUPPLIER_UPDATED_SUBSCRIPTION, {
        onData({ data: { data } }) {
            if (!data) {
                return;
            }
            const { supplierUpdated } = data;
            if (supplierUpdated?._id === supplierId) {
                setSupplierUpdated(supplierUpdated);
            }
        },
    });

    if (supplierLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (supplierError || !supplierData) {
        throw new Error('Une erreur est survenue lors de la récupération du fournisseur');
    }

    const { detailedSupplier } = supplierData;

    if (!detailedSupplier && !supplierUpdated) {
        throw new Error(`Aucun fournisseur avec l'id ${supplierId} n'a été trouvé`);
    }

    const supplier = supplierUpdated?._id === supplierId ? supplierUpdated : detailedSupplier;

    return (
        <Container>
            {supplier.odooSupplierId ? (
                <SupplierGlobalForm key={supplier._id} detailedSupplier={supplier} />
            ) : (
                <SupplierOdooOnboarding key={supplier._id} supplier={supplier} onCompleted={setSupplierUpdated} />
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
